import { useState } from "react";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth-context";
import { useGetCreditBalance } from "@workspace/api-client-react";
import i18n from "@/i18n";

/**
 * Mirrors the backend's credit-warning email sweep (billingWarningEmails.ts):
 * LOW_CREDIT_THRESHOLD = 200 and CREDIT_EXPIRY_WARN_DAYS = 3. This banner is
 * the second, more visible in-app reminder for the same two conditions.
 */
export const CREDIT_REMINDER_LOW_THRESHOLD = 200;
export const CREDIT_EXPIRY_REMINDER_WINDOW_DAYS = 3;

const DAY_MS = 24 * 60 * 60 * 1000;
const STORAGE_KEY = "creative-adapt-credit-reminder-dismissed";
const MAX_STORED_DISMISSALS = 20;

export interface CreditReminderLot {
  id: string;
  remaining: number;
  creditedAt: string;
  expiresAt: string;
}

export type CreditReminder =
  | { kind: "low"; key: string; total: number }
  | { kind: "expiry"; key: string; expiringAmount: number; expiresAtMs: number };

function parseMs(value: string): number {
  return new Date(value).getTime();
}

/**
 * Decide which reminder (if any) should show. Exported for tests.
 *
 * Dismissal keys are chosen so a NEW lot or billing period warns again:
 * - low-balance is keyed to the newest lot's creditedAt — a top-up (new lot)
 *   resets the dismissal, so a user who tops up and drains down again is
 *   re-warned;
 * - lot expiry is keyed to the expiring lot's id — a fresh lot expiring later
 *   gets its own warning.
 *
 * Low balance takes priority (it blocks work sooner); if it was dismissed but
 * an undismissed expiry warning applies, the expiry reminder shows instead.
 */
export function getCreditReminder(args: {
  total: number | undefined;
  lots: CreditReminderLot[] | undefined;
  nowMs: number;
  dismissedKeys: string[];
}): CreditReminder | null {
  const { total, lots, nowMs, dismissedKeys } = args;
  if (total === undefined || !lots) return null;

  const candidates: CreditReminder[] = [];

  if (total <= CREDIT_REMINDER_LOW_THRESHOLD) {
    let newestCreditedMs = 0;
    for (const lot of lots) {
      const ms = parseMs(lot.creditedAt);
      if (!Number.isNaN(ms) && ms > newestCreditedMs) newestCreditedMs = ms;
    }
    candidates.push({
      kind: "low",
      key: `low:${newestCreditedMs || "none"}`,
      total,
    });
  }

  const horizon = nowMs + CREDIT_EXPIRY_REMINDER_WINDOW_DAYS * DAY_MS;
  let expiringAmount = 0;
  let earliest: { id: string; expiresAtMs: number } | null = null;
  for (const lot of lots) {
    if (lot.remaining <= 0) continue;
    const expiresMs = parseMs(lot.expiresAt);
    if (Number.isNaN(expiresMs)) continue;
    if (expiresMs <= nowMs || expiresMs > horizon) continue;
    expiringAmount += lot.remaining;
    if (!earliest || expiresMs < earliest.expiresAtMs) {
      earliest = { id: lot.id, expiresAtMs: expiresMs };
    }
  }
  if (earliest) {
    candidates.push({
      kind: "expiry",
      key: `expiry:${earliest.id}`,
      expiringAmount,
      expiresAtMs: earliest.expiresAtMs,
    });
  }

  for (const candidate of candidates) {
    if (!dismissedKeys.includes(candidate.key)) return candidate;
  }
  return null;
}

function readDismissedKeys(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : [];
  } catch {
    return [];
  }
}

function fmtDate(ms: number): string {
  if (Number.isNaN(ms)) return "—";
  return new Intl.DateTimeFormat("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(ms));
}

function fmtNumber(value: number): string {
  const locale = i18n.language === "vi" ? "vi-VN" : "en-US";
  return value.toLocaleString(locale);
}

/**
 * Dismissible in-app banner shown to logged-in members when the account's
 * credit balance is low (≤200, mirroring the warning email) or a credit lot
 * expires within ~3 days. Deep-links to /purchase for a top-up. Reviewer-only
 * users never see it (they cannot access the purchase flow).
 */
export function CreditWarningReminder() {
  const { t } = useTranslation("billing");
  const { user } = useAuth();
  const isMember = !!user && !user.isReviewer;
  const { data: balance } = useGetCreditBalance({
    query: { enabled: isMember } as never,
  });
  const [dismissedKeys, setDismissedKeys] = useState(readDismissedKeys);

  if (!isMember) return null;

  const reminder = getCreditReminder({
    total: balance?.total,
    lots: balance?.lots,
    nowMs: Date.now(),
    dismissedKeys,
  });
  if (!reminder) return null;

  const dismiss = () => {
    const next = [...dismissedKeys, reminder.key].slice(-MAX_STORED_DISMISSALS);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore storage failures; the banner just reappears next load
    }
    setDismissedKeys(next);
  };

  const title =
    reminder.kind === "low"
      ? t("creditReminder.lowTitle")
      : t("creditReminder.expiryTitle");
  const message =
    reminder.kind === "low"
      ? t("creditReminder.lowMessage", {
          total: fmtNumber(reminder.total),
          threshold: fmtNumber(CREDIT_REMINDER_LOW_THRESHOLD),
        })
      : t("creditReminder.expiryMessage", {
          amount: fmtNumber(reminder.expiringAmount),
          date: fmtDate(reminder.expiresAtMs),
        });

  return (
    <div
      data-testid="banner-credit-reminder"
      role="region"
      aria-label={title}
      className="flex flex-wrap items-center gap-x-4 gap-y-2"
      style={{
        marginBottom: 18,
        padding: "13px 16px",
        borderRadius: 14,
        border: "1px solid rgba(255,180,60,.4)",
        background: "linear-gradient(135deg,rgba(255,180,60,.14),rgba(255,138,50,.08))",
      }}
    >
      <span style={{ color: "#FFC15A", flex: "none", display: "flex" }} aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          width="19"
          height="19"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </span>
      <div style={{ flex: "1 1 320px", minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "#FFD080" }}>{title}</div>
        <p style={{ margin: "2px 0 0", fontSize: 12.5, lineHeight: 1.55, color: "#C0B2A4" }}>
          {message}
        </p>
      </div>
      <div className="flex items-center" style={{ gap: 8, flex: "none" }}>
        <Link href="/purchase" asChild>
          <a
            data-testid="link-credit-reminder-topup"
            className="transition-[filter] hover:brightness-110"
            style={{
              padding: "8px 15px",
              borderRadius: 10,
              background: "linear-gradient(135deg,#FF8A2B,#FF5900)",
              color: "#fff",
              fontSize: 12.5,
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 6px 14px rgba(255,89,0,.3)",
              whiteSpace: "nowrap",
            }}
          >
            {t("creditReminder.topup")}
          </a>
        </Link>
        <button
          type="button"
          data-testid="button-credit-reminder-dismiss"
          aria-label={t("creditReminder.dismiss")}
          title={t("creditReminder.dismiss")}
          onClick={dismiss}
          className="flex items-center justify-center"
          style={{
            width: 30,
            height: 30,
            borderRadius: 9,
            border: "1px solid rgba(255,255,255,.14)",
            background: "rgba(255,255,255,.05)",
            color: "#C0B2A4",
            cursor: "pointer",
            flex: "none",
            transition: ".15s",
          }}
        >
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
}
